import React, { useEffect, useState } from "react";
import {
  Layout,
  Row,
  Col,
  Space,
  Button,
  Input,
  Select,
  Card,
  Tooltip,
  Typography,
  Spin,
  message,
  Empty,
  Modal,
} from "antd";
import {
  EditOutlined,
  EyeOutlined,
  MessageOutlined,
  CopyOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { getAIAssistants, getAIAssistant, updateAIAssistant, createAIConversation } from "./resources";

const { Header, Content } = Layout;
const { Title } = Typography;

interface ExploreProps {
  conversationTitles: string[];
  layoutStyle: any;
  headerStyle: any;
  PanelStyle: any;
  onCreateClick: (prefillAssistant?: Partial<Assistant>) => void;
  onChatClick: () => void;
  onNewConversation?: (conv: any) => void;
  onRefreshConversations?: () => void;
}

interface Assistant {
  id?: string;
  name: string;
  description?: string;
  instructions?: string;
  user_id?: string;
  user?: {
    id: string;
    name: string;
    [key: string]: any;
  };
}

const ExploreAssistants: React.FC<ExploreProps> = ({
  conversationTitles,
  layoutStyle,
  headerStyle,
  PanelStyle,
  onCreateClick,
  onChatClick,
  onNewConversation,
  onRefreshConversations,
}) => {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterType, setFilterType] = useState<'all' | 'mine'>('all');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState<Assistant | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editInstructions, setEditInstructions] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [creatingChatId, setCreatingChatId] = useState<string | null>(null);

  // Handler for cloning an assistant (open create modal with prefilled data)
  const handleCloneAssistant = (assistant: Assistant) => {
    // Remove id and user info to avoid accidental overwrite
    const { id, user, ...rest } = assistant;
    onCreateClick({ ...rest });
  };

  // Handler for creating a new chat for an assistant (copied from ScreeningAssistantPanel)
  const handleNewChat = async (assistant: Assistant) => {
    if (!assistant?.id) {
      message.error("No assistant selected for new chat.");
      return;
    }
    setCreatingChatId(assistant.id);
    try {
      const userData = localStorage.getItem("userData");
      let userId = null;
      if (userData) {
        try {
          userId = JSON.parse(userData)?.id;
        } catch {}
      }
      if (!userId) {
        message.error("No user ID found.");
        setCreatingChatId(null);
        return;
      }
      const payload = {
        title: "Untitled",
        user_id: userId,
        text: { messages: [] },
      };
      const res = await createAIConversation(assistant.id, payload);
      if (res?.data) {
        message.success("New chat created");
        if (onNewConversation) onNewConversation(res.data);
        if (onRefreshConversations) onRefreshConversations();
        if (onChatClick) onChatClick();
      } else {
        message.error("Failed to create new chat");
      }
    } catch (err) {
      message.error("Failed to create new chat");
      console.error(err);
    } finally {
      setCreatingChatId(null);
    }
  };

  // Get user_id from localStorage (if available)
  let userId: string | null = null;
  try {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsed = JSON.parse(userData);
      userId = parsed?.id ?? null;
    }
  } catch {}

  useEffect(() => {
    let mounted = true;
    const fetchAssistants = async () => {
      setLoading(true);
      try {
        const res = await getAIAssistants();
        // Expecting shape: { statusCode, data: [...] }
        const list = res?.data ?? res;
        if (mounted) {
          if (Array.isArray(list)) setAssistants(list);
          else setAssistants([]);
        }
      } catch (err) {
        message.error("Failed to load assistants");
        if (mounted) setAssistants([]);
        console.error("getAIAssistants error", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAssistants();
    return () => {
      mounted = false;
    };
  }, []);
  // Expose fetchAssistants for refresh after edit
  const refreshAssistants = async () => {
    setLoading(true);
    try {
      const res = await getAIAssistants();
      const list = res?.data ?? res;
      if (Array.isArray(list)) setAssistants(list);
      else setAssistants([]);
    } catch {
      setAssistants([]);
    } finally {
      setLoading(false);
    }
  };

  const itemsToRender = (assistants.length ? assistants : conversationTitles.map((t, i) => ({ id: String(i), name: t, description: undefined }))) as Assistant[];
  const searchTermLower = searchTerm.trim().toLowerCase();
  let filteredItems = itemsToRender;
  // Apply mine/all filter if userId is available and assistants are loaded
  if (filterType === 'mine' && userId && assistants.length) {
    filteredItems = filteredItems.filter((item) => (item as any).user_id === userId);
  }
  // Apply search filter
  if (searchTermLower) {
    filteredItems = filteredItems.filter((item) => {
      const name = ((item as any).name ?? "").toString().toLowerCase();
      const desc = ((item as any).description ?? (item as any).instructions ?? "").toString().toLowerCase();
      return name.includes(searchTermLower) || desc.includes(searchTermLower);
    });
  }

  // Handler for viewing an assistant
  const handleViewAssistant = async (id: string) => {
    setViewModalOpen(true);
    setViewLoading(true);
    setViewData(null);
    setEditName("");
    setEditDescription("");
    setEditInstructions("");
    try {
      const res = await getAIAssistant(id);
      const data = res?.data ?? res;
      setViewData(data);
      setEditName(data.name || "");
      setEditDescription(data.description || "");
      setEditInstructions(data.instructions || "");
    } catch (err) {
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  // Only allow editing if the logged-in user owns the assistant
  const isOwner = viewData && userId && viewData.user_id === userId;

  // Handle update assistant submit
  const handleUpdateAssistant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewData?.id) return;
    if (!editName.trim() || !editInstructions.trim()) {
      message.error("Name and Instructions are required.");
      return;
    }
    setEditSaving(true);
    try {
      await updateAIAssistant(viewData.id, {
        name: editName,
        description: editDescription,
        instructions: editInstructions,
      });
      message.success("Assistant updated!");
      setViewModalOpen(false);
      await refreshAssistants();
    } catch (err) {
      message.error("Failed to update assistant");
      console.error(err);
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <Layout style={layoutStyle}>
      <Header style={{ ...headerStyle, padding: "3px 10px" }}>
        <Row align="middle" style={{ width: "100%" }}>
          <Col flex="auto"></Col>
          <Col>
            <Space>
              <Button type="primary" icon={<EditOutlined />} onClick={() => onCreateClick()}>
                Create Assistant
              </Button>
            </Space>
          </Col>
        </Row>
      </Header>
      <Content style={{ ...PanelStyle, overflow: "auto", height: "100%" }}>
        <div style={{ padding: 32, textAlign: "center", background: "ghostwhite" }}>
          <Title level={2} style={{ marginBottom: 24 }}>
            Explore Assistants
          </Title>
          <Title level={5}>
            Discover and create custom versions of ChatGPT that combine instructions, extra
            knowledge, and any combination of skills.
          </Title>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, margin: "24px 0" }}>
            <Input.Search
              placeholder="Search assistants..."
              allowClear
              style={{ width: 320 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={(value) => setSearchTerm(value)}
            />
            <Select
              value={filterType}
              style={{ width: 120 }}
              options={[{ value: "mine", label: "Mine" }, { value: "all", label: "All" }]}
              onChange={v => setFilterType(v)}
            />
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              flexWrap: "wrap",
              gap: 24,
              justifyContent: "center",
            }}
          >
            {loading ? (
              <div style={{ width: "100%", textAlign: "center", padding: 40 }}>
                <Spin tip="Loading assistants..." />
              </div>
            ) : (
              filteredItems.length ? (
                filteredItems.map((item, idx) => {
                   const title = (item as Assistant).name ?? String(item.name);
                   const description = (item as Assistant).description ?? (item as any).instructions ?? "";

                   return (
                     <Card key={`${idx}-${title}`} style={{ width: 300, marginBottom: 16 }} actions={[
                       <Tooltip title="View" key="view">
                         <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewAssistant((item as Assistant).id)} />
                       </Tooltip>,
                        <Tooltip title="Chat" key="chat">
                          <Button
                            type="link"
                            icon={<MessageOutlined />}
                            loading={creatingChatId === (item as Assistant).id}
                            onClick={() => handleNewChat(item as Assistant)}
                          />
                        </Tooltip>,
                        <Tooltip title="Clone" key="clone">
                          <Button type="link" icon={<CopyOutlined />} onClick={() => handleCloneAssistant(item as Assistant)} />
                        </Tooltip>,
                     ]}>
                        <Card.Meta
                          title={title}
                          description={
                            <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <RobotOutlined style={{ marginRight: 8 }} />
                                <span>{(item as Assistant).user?.name ? (item as Assistant).user.name : "AI Assistant"}</span>
                              </div>
                              <div style={{ marginTop: 8, fontSize: 13, color: "#888" }}>{description}</div>
                            </div>
                          }
                        />
                      </Card>
                   );
                 })
               ) : (
                <div style={{ width: "100%", padding: 48 }}>
                  <Empty description={searchTerm ? "No assistants match your search" : "No assistants found"} />
                </div>
               )
             )}
           </div>
         </div>
       </Content>
       {/* View/Edit Assistant Modal */}
       <Modal
         open={viewModalOpen}
         onCancel={() => setViewModalOpen(false)}
         footer={null}
         title={viewData?.name || "Assistant Details"}
       >
         {viewLoading ? (
           <Spin tip="Loading..." />
         ) : viewData ? (
           <form style={{ display: 'flex', flexDirection: 'column', gap: 20 }} onSubmit={handleUpdateAssistant}>
             {viewData.user?.name && (
               <div style={{ fontWeight: 500, color: '#555', marginBottom: -8 }}>By {viewData.user.name}</div>
             )}
             <Input
               placeholder="Name your Assistant"
               value={editName}
               onChange={e => setEditName(e.target.value)}
               required
               disabled={!isOwner}
             />
             <Input
               placeholder="Add a short description what this assistant does"
               value={editDescription}
               onChange={e => setEditDescription(e.target.value)}
               disabled={!isOwner}
             />
             <Input.TextArea
               placeholder="Instructions (How should this assistant behave?)"
               value={editInstructions}
               onChange={e => setEditInstructions(e.target.value)}
               rows={4}
               required
               className="dark-placeholder"
               disabled={!isOwner}
             />
             <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
               <Button onClick={() => setViewModalOpen(false)} disabled={editSaving}>
                 Cancel
               </Button>
               <Button type="primary" htmlType="submit" loading={editSaving} disabled={!isOwner}>
                 Save
               </Button>
             </div>
            {!isOwner && (
              <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>
                Only the owner can edit this assistant.
              </div>
            )}
           </form>
         ) : (
           <div>Could not load assistant details.</div>
         )}
       </Modal>
     </Layout>
   );
 };

 export default ExploreAssistants;
